import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isBefore, isAfter, addMonths, subMonths } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import * as analytics from '../analytics';

interface DaysSelectorProps {
  days: number;
  onChangeDays: (days: number) => void;
}

function DaysSelector({ days, onChangeDays }: DaysSelectorProps) {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isSelectingEnd, setIsSelectingEnd] = useState<boolean>(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  const calculateDays = (start: Date | null, end: Date | null): number => {
    if (!start || !end) return 1;
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1; // Include both start and end day
  };

  const handleDateClick = (date: Date) => {
    if (!isSelectingEnd) {
      // First click - selecting start date
      setStartDate(date);
      setEndDate(null);
      setIsSelectingEnd(true);
      onChangeDays(1);
    } else {
      // Second click - selecting end date
      let calculatedDays: number;
      if (date && startDate && isBefore(date, startDate)) {
        // If end date is before start date, swap them
        setStartDate(date);
        setEndDate(startDate);
        calculatedDays = calculateDays(date, startDate);
        onChangeDays(calculatedDays);
      } else {
        setEndDate(date);
        calculatedDays = calculateDays(startDate, date);
        onChangeDays(calculatedDays);
      }
      // Track days selected when range is complete
      analytics.trackDaysSelected(calculatedDays);
      setIsSelectingEnd(false);
    }
  };

  const isDateInRange = (date: Date): boolean => {
    if (!startDate || !endDate) return false;
    return (isSameDay(date, startDate) || isSameDay(date, endDate)) ||
           (isAfter(date, startDate) && isBefore(date, endDate));
  };

  const isStartDate = (date: Date): boolean => {
    return startDate && isSameDay(date, startDate);
  };

  const isEndDate = (date: Date): boolean => {
    return endDate && isSameDay(date, endDate);
  };

  const getCalendarDays = (): (Date | null)[] => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days to fill the grid
    const firstDayOfWeek = start.getDay();
    const paddingStart = Array(firstDayOfWeek).fill(null);
    const paddingEnd = Array(6 - end.getDay()).fill(null);
    
    return [...paddingStart, ...days, ...paddingEnd];
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const getDisplayValue = (): string => {
    if (!startDate && !endDate) return 'Select travel dates';
    if (startDate && !endDate) {
      return `${format(startDate, 'MMM dd, yyyy')} - Select end date`;
    }
    if (startDate && endDate) {
      return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`;
    }
    return '';
  };

  return (
    <Box>
      {/* Date Range Display */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CalendarTodayIcon color="primary" />
          <Typography variant="h6">
            {getDisplayValue()}
          </Typography>
        </Box>
        
        {startDate && !endDate && (
          <Typography variant="body2" color="primary">
            Now select your end date
          </Typography>
        )}
      </Paper>

      {/* Calendar View */}
      <Paper sx={{ p: 2 }}>
        {/* Calendar Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Button onClick={prevMonth} startIcon={<ChevronLeftIcon />}>
            Previous
          </Button>
          <Typography variant="h6">
            {format(currentMonth, 'MMMM yyyy')}
          </Typography>
          <Button onClick={nextMonth} endIcon={<ChevronRightIcon />}>
            Next
          </Button>
        </Box>

        {/* Day Headers */}
        <Box sx={{ display: 'flex', mb: 1 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Box key={day} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Days */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {getCalendarDays().map((day, index) => (
            <Box key={index} sx={{ width: 'calc(100% / 7)', p: 0.5 }}>
              {day ? (
                <Button
                  fullWidth
                  variant={
                    isStartDate(day) || isEndDate(day) ? "contained" :
                    isDateInRange(day) ? "outlined" : "text"
                  }
                  color={
                    isStartDate(day) ? "primary" :
                    isEndDate(day) ? "secondary" : "primary"
                  }
                  onClick={() => handleDateClick(day)}
                  disabled={isBefore(day, new Date())}
                  sx={{
                    minHeight: 40,
                    borderRadius: 1,
                    ...(isDateInRange(day) && !isStartDate(day) && !isEndDate(day) && {
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      '&:hover': {
                        bgcolor: 'primary.main',
                      }
                    })
                  }}
                >
                  {format(day, 'd')}
                </Button>
              ) : (
                <Box sx={{ height: 40 }} />
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {days > 1 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Trip duration: {days} days
        </Typography>
      )}
    </Box>
  );
}

export default DaysSelector; 